// src/clock/ObjectiveClock.ts
import { createChildLogger } from "@retreat/shared";
var WORKER_SCRIPT = `
let intervalId = null;
self.onmessage = function(e) {
  const { command, interval } = e.data;
  if (command === "start") {
    if (intervalId !== null) clearInterval(intervalId);
    intervalId = setInterval(function() {
      self.postMessage({ type: "tick" });
    }, interval || 16);
  } else if (command === "stop") {
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }
};
`;
var DEFAULT_TICK_INTERVAL_MS = 16;
var ObjectiveClock = class {
  log = createChildLogger("ObjectiveClock");
  /** The wall-clock anchor: Date.now() at the moment start() was called, adjusted for offset */
  startAnchor = 0;
  /** Registered tick callbacks */
  tickCallbacks = [];
  /** Worker instance (null when not running) */
  worker = null;
  /** Whether ticking is active */
  isRunning = false;
  /** Tick interval in milliseconds */
  tickIntervalMs;
  /** Fallback interval ID for non-Worker environments (Node.js tests) */
  fallbackIntervalId = null;
  /** Whether we're in a Worker-capable environment */
  hasWorkerSupport;
  /** Epoch guard function (INV-021) */
  epochGuard = null;
  constructor(tickIntervalMs = DEFAULT_TICK_INTERVAL_MS) {
    this.tickIntervalMs = tickIntervalMs;
    this.hasWorkerSupport = typeof Worker !== "undefined" && typeof Blob !== "undefined";
  }
  /**
   * Start ticking from a given offset.
   *
   * Sets the wall-clock anchor so that elapsed time at this moment equals offsetSeconds.
   * Formula: startAnchor = Date.now() - (offsetSeconds * 1000)
   *
   * This means on the very next tick:
   *   elapsed = (Date.now() - startAnchor) / 1000 ≈ offsetSeconds
   *
   * @param offsetSeconds - The elapsed time (in seconds) to resume from.
   */
  start(offsetSeconds) {
    this.startAnchor = Date.now() - offsetSeconds * 1e3;
    this.isRunning = true;
    this.log.info("start", {
      offsetSeconds,
      startAnchor: this.startAnchor,
      useWorker: this.hasWorkerSupport
    });
    this.startTicking();
  }
  /**
   * Pause the clock. Stops the worker/interval but preserves the anchor
   * so resume (via start()) picks up where we left off.
   */
  pause() {
    this.log.flow("pause", { isRunning: this.isRunning });
    this.isRunning = false;
    this.stopTicking();
  }
  /**
   * Full stop. Terminates worker, clears all state.
   */
  stop() {
    this.log.info("stop");
    this.isRunning = false;
    this.tickCallbacks = [];
    this.stopTicking();
    this.startAnchor = 0;
  }
  /**
   * Register a tick callback. Called on every tick with the current elapsed
   * time in seconds, computed from the wall clock.
   *
   * @param cb - Callback receiving elapsed seconds since start.
   */
  onTick(cb) {
    this.tickCallbacks.push(cb);
    this.log.flow("onTick registered", {
      listeners: this.tickCallbacks.length
    });
  }
  /**
   * Set epoch guard function (INV-021: Async Callback Epoch Validation)
   */
  setEpochGuard(guard) {
    this.epochGuard = guard;
  }
  /**
   * Cleanup: terminate worker and release resources.
   */
  destroy() {
    this.stop();
    this.log.info("destroy - resources released");
  }
  // --- Private: Tick Delivery ---
  /**
   * Start the tick source (Worker or fallback setInterval).
   * Each tick computes elapsed from Date.now() — never accumulates.
   */
  startTicking() {
    this.stopTicking();
    const capturedEpoch = this.epochGuard ? this.epochGuard() : 0;
    if (this.hasWorkerSupport) {
      this.startWorkerTicking(capturedEpoch);
    } else {
      this.startFallbackTicking(capturedEpoch);
    }
  }
  /**
   * Web Worker tick source: un-throttled even when tab is backgrounded.
   */
  startWorkerTicking(capturedEpoch) {
    try {
      const blob = new Blob([WORKER_SCRIPT], {
        type: "application/javascript"
      });
      const url = URL.createObjectURL(blob);
      this.worker = new Worker(url);
      URL.revokeObjectURL(url);
      this.worker.onmessage = () => {
        if (!this.isRunning) return;
        if (this.epochGuard && capturedEpoch !== this.epochGuard()) return;
        this.deliverTick();
      };
      this.worker.onerror = (err) => {
        this.log.flow("Worker error \u2014 falling back to setInterval", {
          message: err.message
        });
        this.stopTicking();
        this.startFallbackTicking(capturedEpoch);
      };
      this.worker.postMessage({
        command: "start",
        interval: this.tickIntervalMs
      });
      this.log.flow("Worker tick source started", {
        intervalMs: this.tickIntervalMs
      });
    } catch (err) {
      this.log.flow("Worker creation failed \u2014 falling back to setInterval", {
        error: err instanceof Error ? err.message : String(err)
      });
      this.startFallbackTicking(capturedEpoch);
    }
  }
  /**
   * Fallback tick source for environments without Worker support (Node.js tests).
   */
  startFallbackTicking(capturedEpoch) {
    this.fallbackIntervalId = setInterval(() => {
      if (!this.isRunning) return;
      if (this.epochGuard && capturedEpoch !== this.epochGuard()) return;
      this.deliverTick();
    }, this.tickIntervalMs);
    this.log.flow("Fallback tick source started", {
      intervalMs: this.tickIntervalMs
    });
  }
  /**
   * Compute elapsed from wall clock and deliver to all listeners.
   *
   * THE 7-YEAR AUDITOR GUARANTEE:
   * elapsed is ALWAYS computed as (Date.now() - anchor) / 1000.
   * We never increment a counter. Over 220,752,000 seconds (7 years),
   * this remains accurate to within 1ms of the system clock.
   */
  deliverTick() {
    const elapsed = (Date.now() - this.startAnchor) / 1e3;
    for (const cb of this.tickCallbacks) {
      if (!this.isRunning) break;
      cb(elapsed);
    }
  }
  /**
   * Stop all tick sources. Does NOT clear callbacks or anchor.
   */
  stopTicking() {
    if (this.worker) {
      this.worker.postMessage({ command: "stop" });
      this.worker.terminate();
      this.worker = null;
    }
    if (this.fallbackIntervalId !== null) {
      clearInterval(this.fallbackIntervalId);
      this.fallbackIntervalId = null;
    }
  }
};

// src/planes/ExecutionPlanes.ts
import { createChildLogger as createChildLogger2 } from "@retreat/shared";
var log = createChildLogger2("ExecutionPlane");
var StubExecutionPlane = class {
  constructor(planeName) {
    this.planeName = planeName;
  }
  planeName;
  execute(event) {
    log.debug(`[${this.planeName}.execute]`, { eventId: event.id, payload: event.payload });
  }
  sync(offsetSeconds) {
    log.debug(`[${this.planeName}.sync]`, { offsetSeconds });
  }
  stopAll() {
    log.debug(`[${this.planeName}.stopAll]`);
  }
  async prepare(events) {
    log.debug(`[${this.planeName}.prepare]`, { eventCount: events.length });
  }
};
var AudioPlane = class extends StubExecutionPlane {
  constructor() {
    super("AudioPlane");
  }
};
var SystemPlane = class extends StubExecutionPlane {
  constructor() {
    super("SystemPlane");
  }
};
var UIPlane = class extends StubExecutionPlane {
  constructor() {
    super("UIPlane");
  }
};

// src/lifecycle/AppLifecycleManager.ts
import { createChildLogger as createChildLogger3 } from "@retreat/shared";
import { RecoveryStrategy } from "@retreat/core";
var AppLifecycleManager = class {
  log = createChildLogger3("AppLifecycleManager");
  sessionState = null;
  constructor() {
    this.log.trace("[SCREAM] AppLifecycleManager: Constructor");
  }
  /**
   * Called when app comes to foreground.
   * Calculates recovery delta and dispatches appropriate action.
   */
  onForeground() {
    this.log.trace("[SCREAM] AppLifecycleManager: onForeground()");
    if (!this.sessionState) {
      this.log.trace(
        "[SCREAM] AppLifecycleManager: No session state, returning none"
      );
      return { action: "none", delta: 0 };
    }
    const now = Date.now();
    const sessionStartTime = this.sessionState.absoluteStartTime;
    const delta = Math.floor((now - sessionStartTime) / 1e3);
    this.log.trace(
      `[SCREAM] AppLifecycleManager: Delta calculated (now=${now}, startTime=${sessionStartTime}, delta=${delta}s)`
    );
    const strategy = this.sessionState.recoveryStrategy;
    if (strategy === RecoveryStrategy.WALL_CLOCK) {
      this.log.trace(
        `[SCREAM] AppLifecycleManager: WALL_CLOCK strategy  sync(${delta})`
      );
      return { action: "sync", delta };
    }
    if (strategy === RecoveryStrategy.RESUME_PROGRESS) {
      const lastOffset = this.sessionState.lastOffset;
      this.log.trace(
        `[SCREAM] AppLifecycleManager: RESUME_PROGRESS strategy  resume(offset=${lastOffset})`
      );
      return { action: "resume", delta: lastOffset };
    }
    this.log.trace(
      `[SCREAM] AppLifecycleManager: Unknown strategy (${strategy}), returning none`
    );
    return { action: "none", delta: 0 };
  }
  /**
   * Called when app goes to background.
   * Placeholder for future cleanup logic.
   */
  onBackground() {
    this.log.trace("[SCREAM] AppLifecycleManager: onBackground()");
  }
  /**
   * Update the session state (called by engine after recovery).
   */
  setSessionState(state) {
    this.log.trace(
      `[SCREAM] AppLifecycleManager: setSessionState(retreatId=${state.retreatId}, recoveryStrategy=${state.recoveryStrategy})`
    );
    this.sessionState = state;
  }
  /**
   * Get current session state.
   */
  getSessionState() {
    this.log.trace("[SCREAM] AppLifecycleManager: getSessionState()");
    return this.sessionState;
  }
};

// src/lifecycle/AppLifecycleModule.ts
import { createChildLogger as createChildLogger4 } from "@retreat/shared";
import { getRecoveryInstructions } from "@retreat/core";
var AppLifecycleModule = class {
  log = createChildLogger4("AppLifecycleModule");
  manager;
  sessionState = null;
  onRecovery = null;
  onEmergencySave = null;
  isListening = false;
  /**
   * AGENT.md Rule 5.1 (Zero Zombies): Named handler references for cleanup.
   * Stored so removeEventListener receives the EXACT same function reference.
   */
  _visibilityHandler = null;
  _focusHandler = null;
  _blurHandler = null;
  /** INV-021: Epoch guard — set by RetreatManager after initialize(). */
  _epochGuard = null;
  setEpochGuard(isValid) {
    this._epochGuard = isValid;
  }
  constructor() {
    this.manager = new AppLifecycleManager();
    this.log.info("AppLifecycleModule initialized");
  }
  /**
   * Set session state (called by RetreatManager after loading session)
   *
   * @param state Session restored from storage
   */
  setSessionState(state) {
    this.sessionState = state;
    this.manager.setSessionState(state);
    this.log.trace("Session state updated", {
      className: "AppLifecycleModule",
      methodName: "setSessionState",
      action: "session_loaded",
      retreatId: state.retreatId,
      recoveryStrategy: state.recoveryStrategy
    });
  }
  /**
   * Register callback for when recovery is needed
   *
   * @param callback Function to call with recovery instructions
   */
  onRecoveryNeeded(callback) {
    this.onRecovery = callback;
    this.log.trace("Recovery callback registered", {
      className: "AppLifecycleModule",
      methodName: "onRecoveryNeeded",
      action: "callback_registered"
    });
  }
  /**
   * Register callback for emergency saves on background
   * Called when app goes hidden to force-save state (Issue 9.2)
   *
   * @param callback Function to call with session state for forceSave
   */
  setOnEmergencySave(callback) {
    this.onEmergencySave = callback;
    this.log.trace("Emergency save callback registered", {
      className: "AppLifecycleModule",
      methodName: "setOnEmergencySave",
      action: "callback_registered"
    });
  }
  /**
   * Start listening for browser lifecycle events
   * Attaches event listeners for 'visibilitychange', 'focus', etc.
   *
   * AGENT.md Rule 5.1: Handlers are stored as named references
   * so stopListening() can remove the EXACT same reference.
   */
  startListening() {
    if (this.isListening) {
      this.log.trace("Already listening for lifecycle events", {
        className: "AppLifecycleModule",
        methodName: "startListening",
        action: "already_listening"
      });
      return;
    }
    if (typeof document !== "undefined") {
      this._visibilityHandler = () => {
        if (this._epochGuard && !this._epochGuard()) return;
        if (document.hidden) {
          this.handleBackground();
        } else {
          this.handleForeground();
        }
      };
      this._focusHandler = () => {
        if (this._epochGuard && !this._epochGuard()) return;
        this.handleForeground();
      };
      this._blurHandler = () => {
        if (this._epochGuard && !this._epochGuard()) return;
        this.handleBackground();
      };
      document.addEventListener("visibilitychange", this._visibilityHandler);
      window.addEventListener("focus", this._focusHandler);
      window.addEventListener("blur", this._blurHandler);
      this.isListening = true;
      this.log.info("Browser lifecycle listeners attached", {
        className: "AppLifecycleModule",
        methodName: "startListening",
        action: "listeners_attached"
      });
    }
  }
  /**
   * Stop listening for browser lifecycle events.
   *
   * AGENT.md Rule 5.1 (Zero Zombies): Uses the stored named references
   * so removeEventListener actually removes the correct listener.
   */
  stopListening() {
    if (!this.isListening) {
      return;
    }
    if (typeof document !== "undefined") {
      if (this._visibilityHandler) {
        document.removeEventListener(
          "visibilitychange",
          this._visibilityHandler
        );
        this._visibilityHandler = null;
      }
      if (this._focusHandler) {
        window.removeEventListener("focus", this._focusHandler);
        this._focusHandler = null;
      }
      if (this._blurHandler) {
        window.removeEventListener("blur", this._blurHandler);
        this._blurHandler = null;
      }
      this.isListening = false;
      this.log.info("Browser lifecycle listeners detached", {
        className: "AppLifecycleModule",
        methodName: "stopListening",
        action: "listeners_detached"
      });
    }
  }
  /**
   * Handle foreground event (app becomes visible/focused)
   * Coordinates with RecoveryStrategyFactory to determine recovery action
   *
   * Silence Rule: No Toast notifications here - just state recovery
   */
  handleForeground() {
    this.log.flow("App foreground detected", {
      className: "AppLifecycleModule",
      methodName: "handleForeground",
      action: "foreground"
    });
    if (!this.sessionState) {
      this.log.trace("No session state available for recovery", {
        className: "AppLifecycleModule",
        methodName: "handleForeground",
        action: "no_session"
      });
      return;
    }
    const recovery = getRecoveryInstructions({
      recoveryStrategy: this.sessionState.recoveryStrategy,
      absoluteStartTime: this.sessionState.absoluteStartTime,
      lastOffset: this.sessionState.lastOffset
    });
    this.log.flow("Recovery instructions generated", {
      className: "AppLifecycleModule",
      methodName: "handleForeground",
      action: "recovery_generated",
      recoveryAction: recovery.action,
      description: recovery.description
    });
    if (this.onRecovery) {
      this.onRecovery(recovery);
    }
  }
  /**
   * Handle background event (app goes hidden/blurred)
   * Trigger emergency save callback to persist state (Issue 9.2)
   */
  handleBackground() {
    this.log.flow("App background detected", {
      className: "AppLifecycleModule",
      methodName: "handleBackground",
      action: "background"
    });
    this.manager.onBackground();
    if (this.sessionState && this.onEmergencySave) {
      const result = this.onEmergencySave(this.sessionState);
      if (result && typeof result.catch === "function") {
        result.catch((err) => {
          this.log.trace("Emergency save failed", { error: err });
        });
      }
    }
  }
  /**
   * Cleanup: Stop listening and clear state
   */
  destroy() {
    this.stopListening();
    this.sessionState = null;
    this.onRecovery = null;
    this.onEmergencySave = null;
    this.log.info("AppLifecycleModule destroyed", {
      className: "AppLifecycleModule",
      methodName: "destroy",
      action: "cleanup"
    });
  }
};

// src/persistence/NullPersistenceProvider.ts
import { createChildLogger as createChildLogger5 } from "@retreat/shared";
var NullPersistenceProvider = class {
  log = createChildLogger5("NullPersistenceProvider");
  name = "NullPersistenceProvider";
  isLocal = false;
  /**
   * Always available (fallback pattern).
   */
  async isAvailable() {
    this.log.trace("[SCREAM] NullPersistenceProvider.isAvailable: true");
    return true;
  }
  /**
   * No-op save. Just log that we received it.
   */
  async save(_key, data) {
    this.log.trace(
      `[SCREAM] NullPersistenceProvider.save: offset=${data.lastOffset} (discarding)`
    );
  }
  /**
   * No-op load. Always return null.
   */
  async load(_key) {
    this.log.trace("[SCREAM] NullPersistenceProvider.load: no data");
    return null;
  }
  /**
   * No-op clear. Just log.
   */
  async clear(_key) {
    this.log.trace("[SCREAM] NullPersistenceProvider.clear: (noop)");
  }
};

// src/persistence/LocalStorageProvider.ts
import { PersistedSessionStateSchema, PERSISTENCE_ERRORS } from "@retreat/core";
import { createChildLogger as createChildLogger6 } from "@retreat/shared";
var LocalStorageProvider = class {
  log = createChildLogger6("LocalStorageProvider");
  name = "LocalStorageProvider";
  isLocal = true;
  isQuotaExceeded = false;
  async isAvailable() {
    if (this.isQuotaExceeded) {
      return false;
    }
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        return false;
      }
      const testKey = "__retreat_quota_test__";
      window.localStorage.setItem(testKey, "1");
      window.localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.code === 22) {
        this.isQuotaExceeded = true;
        this.log.trace("Quota exceeded", {
          error: PERSISTENCE_ERRORS.QUOTA_EXCEEDED
        });
        return false;
      }
      return false;
    }
  }
  async save(key, data) {
    if (!window.localStorage) {
      throw new Error("LocalStorage not available");
    }
    try {
      const json = JSON.stringify(data);
      window.localStorage.setItem(key, json);
      this.log.flow("LocalStorage SAVE", {
        key,
        offset: data.lastOffset,
        events: data.executedEventIds.length
      });
    } catch (error) {
      if (error instanceof DOMException && error.code === 22) {
        this.isQuotaExceeded = true;
        this.log.flow("Quota exceeded on save", {
          error: PERSISTENCE_ERRORS.QUOTA_EXCEEDED
        });
        throw error;
      }
      throw error;
    }
  }
  async load(key) {
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        return null;
      }
      const json = window.localStorage.getItem(key);
      if (!json) {
        return null;
      }
      const raw = JSON.parse(json);
      const result = PersistedSessionStateSchema.safeParse(raw);
      if (!result.success) {
        this.log.trace("Restore schema mismatch \u2014 discarding stale state", {
          key,
          issues: result.error.issues.map((i) => i.message)
        });
        return null;
      }
      this.log.flow("LocalStorage RESTORE", { key });
      return result.data;
    } catch (error) {
      this.log.trace("Restore error", { key, error });
      return null;
    }
  }
  async clear(key) {
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        return;
      }
      window.localStorage.removeItem(key);
      this.log.trace("LocalStorage cleared", { key });
    } catch (error) {
      this.log.trace("Clear error", { key, error });
    }
  }
};

// ../../node_modules/idb/build/index.js
var instanceOfAny = (object, constructors) => constructors.some((c) => object instanceof c);
var idbProxyableTypes;
var cursorAdvanceMethods;
function getIdbProxyableTypes() {
  return idbProxyableTypes || (idbProxyableTypes = [
    IDBDatabase,
    IDBObjectStore,
    IDBIndex,
    IDBCursor,
    IDBTransaction
  ]);
}
function getCursorAdvanceMethods() {
  return cursorAdvanceMethods || (cursorAdvanceMethods = [
    IDBCursor.prototype.advance,
    IDBCursor.prototype.continue,
    IDBCursor.prototype.continuePrimaryKey
  ]);
}
var transactionDoneMap = /* @__PURE__ */ new WeakMap();
var transformCache = /* @__PURE__ */ new WeakMap();
var reverseTransformCache = /* @__PURE__ */ new WeakMap();
function promisifyRequest(request) {
  const promise = new Promise((resolve, reject) => {
    const unlisten = () => {
      request.removeEventListener("success", success);
      request.removeEventListener("error", error);
    };
    const success = () => {
      resolve(wrap(request.result));
      unlisten();
    };
    const error = () => {
      reject(request.error);
      unlisten();
    };
    request.addEventListener("success", success);
    request.addEventListener("error", error);
  });
  reverseTransformCache.set(promise, request);
  return promise;
}
function cacheDonePromiseForTransaction(tx) {
  if (transactionDoneMap.has(tx))
    return;
  const done = new Promise((resolve, reject) => {
    const unlisten = () => {
      tx.removeEventListener("complete", complete);
      tx.removeEventListener("error", error);
      tx.removeEventListener("abort", error);
    };
    const complete = () => {
      resolve();
      unlisten();
    };
    const error = () => {
      reject(tx.error || new DOMException("AbortError", "AbortError"));
      unlisten();
    };
    tx.addEventListener("complete", complete);
    tx.addEventListener("error", error);
    tx.addEventListener("abort", error);
  });
  transactionDoneMap.set(tx, done);
}
var idbProxyTraps = {
  get(target, prop, receiver) {
    if (target instanceof IDBTransaction) {
      if (prop === "done")
        return transactionDoneMap.get(target);
      if (prop === "store") {
        return receiver.objectStoreNames[1] ? void 0 : receiver.objectStore(receiver.objectStoreNames[0]);
      }
    }
    return wrap(target[prop]);
  },
  set(target, prop, value) {
    target[prop] = value;
    return true;
  },
  has(target, prop) {
    if (target instanceof IDBTransaction && (prop === "done" || prop === "store")) {
      return true;
    }
    return prop in target;
  }
};
function replaceTraps(callback) {
  idbProxyTraps = callback(idbProxyTraps);
}
function wrapFunction(func) {
  if (getCursorAdvanceMethods().includes(func)) {
    return function(...args) {
      func.apply(unwrap(this), args);
      return wrap(this.request);
    };
  }
  return function(...args) {
    return wrap(func.apply(unwrap(this), args));
  };
}
function transformCachableValue(value) {
  if (typeof value === "function")
    return wrapFunction(value);
  if (value instanceof IDBTransaction)
    cacheDonePromiseForTransaction(value);
  if (instanceOfAny(value, getIdbProxyableTypes()))
    return new Proxy(value, idbProxyTraps);
  return value;
}
function wrap(value) {
  if (value instanceof IDBRequest)
    return promisifyRequest(value);
  if (transformCache.has(value))
    return transformCache.get(value);
  const newValue = transformCachableValue(value);
  if (newValue !== value) {
    transformCache.set(value, newValue);
    reverseTransformCache.set(newValue, value);
  }
  return newValue;
}
var unwrap = (value) => reverseTransformCache.get(value);
function openDB(name, version, { blocked, upgrade, blocking, terminated } = {}) {
  const request = indexedDB.open(name, version);
  const openPromise = wrap(request);
  if (upgrade) {
    request.addEventListener("upgradeneeded", (event) => {
      upgrade(wrap(request.result), event.oldVersion, event.newVersion, wrap(request.transaction), event);
    });
  }
  if (blocked) {
    request.addEventListener("blocked", (event) => blocked(
      // Casting due to https://github.com/microsoft/TypeScript-DOM-lib-generator/pull/1405
      event.oldVersion,
      event.newVersion,
      event
    ));
  }
  openPromise.then((db) => {
    if (terminated)
      db.addEventListener("close", () => terminated());
    if (blocking) {
      db.addEventListener("versionchange", (event) => blocking(event.oldVersion, event.newVersion, event));
    }
  }).catch(() => {
  });
  return openPromise;
}
var readMethods = ["get", "getKey", "getAll", "getAllKeys", "count"];
var writeMethods = ["put", "add", "delete", "clear"];
var cachedMethods = /* @__PURE__ */ new Map();
function getMethod(target, prop) {
  if (!(target instanceof IDBDatabase && !(prop in target) && typeof prop === "string")) {
    return;
  }
  if (cachedMethods.get(prop))
    return cachedMethods.get(prop);
  const targetFuncName = prop.replace(/FromIndex$/, "");
  const useIndex = prop !== targetFuncName;
  const isWrite = writeMethods.includes(targetFuncName);
  if (
    // Bail if the target doesn't exist on the target. Eg, getAll isn't in Edge.
    !(targetFuncName in (useIndex ? IDBIndex : IDBObjectStore).prototype) || !(isWrite || readMethods.includes(targetFuncName))
  ) {
    return;
  }
  const method = async function(storeName, ...args) {
    const tx = this.transaction(storeName, isWrite ? "readwrite" : "readonly");
    let target2 = tx.store;
    if (useIndex)
      target2 = target2.index(args.shift());
    return (await Promise.all([
      target2[targetFuncName](...args),
      isWrite && tx.done
    ]))[0];
  };
  cachedMethods.set(prop, method);
  return method;
}
replaceTraps((oldTraps) => ({
  ...oldTraps,
  get: (target, prop, receiver) => getMethod(target, prop) || oldTraps.get(target, prop, receiver),
  has: (target, prop) => !!getMethod(target, prop) || oldTraps.has(target, prop)
}));
var advanceMethodProps = ["continue", "continuePrimaryKey", "advance"];
var methodMap = {};
var advanceResults = /* @__PURE__ */ new WeakMap();
var ittrProxiedCursorToOriginalProxy = /* @__PURE__ */ new WeakMap();
var cursorIteratorTraps = {
  get(target, prop) {
    if (!advanceMethodProps.includes(prop))
      return target[prop];
    let cachedFunc = methodMap[prop];
    if (!cachedFunc) {
      cachedFunc = methodMap[prop] = function(...args) {
        advanceResults.set(this, ittrProxiedCursorToOriginalProxy.get(this)[prop](...args));
      };
    }
    return cachedFunc;
  }
};
async function* iterate(...args) {
  let cursor = this;
  if (!(cursor instanceof IDBCursor)) {
    cursor = await cursor.openCursor(...args);
  }
  if (!cursor)
    return;
  cursor = cursor;
  const proxiedCursor = new Proxy(cursor, cursorIteratorTraps);
  ittrProxiedCursorToOriginalProxy.set(proxiedCursor, cursor);
  reverseTransformCache.set(proxiedCursor, unwrap(cursor));
  while (cursor) {
    yield proxiedCursor;
    cursor = await (advanceResults.get(proxiedCursor) || cursor.continue());
    advanceResults.delete(proxiedCursor);
  }
}
function isIteratorProp(target, prop) {
  return prop === Symbol.asyncIterator && instanceOfAny(target, [IDBIndex, IDBObjectStore, IDBCursor]) || prop === "iterate" && instanceOfAny(target, [IDBIndex, IDBObjectStore]);
}
replaceTraps((oldTraps) => ({
  ...oldTraps,
  get(target, prop, receiver) {
    if (isIteratorProp(target, prop))
      return iterate;
    return oldTraps.get(target, prop, receiver);
  },
  has(target, prop) {
    return isIteratorProp(target, prop) || oldTraps.has(target, prop);
  }
}));

// src/persistence/IndexedDBProvider.ts
import { PersistedSessionStateSchema as PersistedSessionStateSchema2, PERSISTENCE_ERRORS as PERSISTENCE_ERRORS2 } from "@retreat/core";
import { createChildLogger as createChildLogger7 } from "@retreat/shared";
var DB_NAME = "retreat-sessions";
var STORE_NAME = "sessions";
var DB_VERSION = 1;
var IndexedDBProvider = class {
  log = createChildLogger7("IndexedDBProvider");
  name = "IndexedDBProvider";
  isLocal = true;
  db = null;
  isQuotaExceeded = false;
  initPromise = null;
  /**
   * Initialize IndexedDB connection.
   * Called lazily on first operation.
   */
  async init() {
    if (this.db) return;
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade: (db) => {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
      blocked: () => {
        this.log.trace(
          "IndexedDB version change blocked - another connection is open"
        );
      },
      blocking: () => {
        this.log.trace(
          "IndexedDB connection is blocking a version change in another tab"
        );
      }
    }).then((database) => {
      this.db = database;
    }).catch((error) => {
      this.log.trace("IndexedDB initialization failed", { error });
      this.db = null;
      this.initPromise = null;
      throw error;
    });
    return this.initPromise.then(() => {
      this.initPromise = null;
    });
  }
  async isAvailable() {
    if (this.isQuotaExceeded) {
      return false;
    }
    try {
      if (typeof window === "undefined" || !window.indexedDB) {
        return false;
      }
      await this.init();
      return this.db !== null;
    } catch (error) {
      if (error instanceof DOMException && error.name === "QuotaExceededError") {
        this.isQuotaExceeded = true;
        this.log.trace("Quota exceeded", {
          error: PERSISTENCE_ERRORS2.QUOTA_EXCEEDED
        });
      }
      return false;
    }
  }
  async save(key, data) {
    try {
      await this.init();
      if (!this.db) {
        throw new Error("IndexedDB not initialized");
      }
      await this.db.put(STORE_NAME, data, key);
      this.log.flow("IndexedDB SAVE", {
        key,
        offset: data.lastOffset,
        events: data.executedEventIds.length
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "QuotaExceededError") {
        this.isQuotaExceeded = true;
        this.log.flow("Quota exceeded", {
          error: PERSISTENCE_ERRORS2.QUOTA_EXCEEDED
        });
        throw error;
      }
      this.log.trace("IndexedDB save failed", { key, error });
      throw error;
    }
  }
  async load(key) {
    try {
      await this.init();
      if (!this.db) {
        return null;
      }
      const data = await this.db.get(STORE_NAME, key);
      if (!data) {
        return null;
      }
      const result = PersistedSessionStateSchema2.safeParse(data);
      if (!result.success) {
        this.log.trace(
          "IndexedDB LOAD: schema mismatch \u2014 discarding stale state",
          {
            key,
            issues: result.error.issues.map((i) => i.message)
          }
        );
        return null;
      }
      this.log.flow("IndexedDB LOAD", {
        key,
        offset: result.data.lastOffset,
        events: result.data.executedEventIds.length
      });
      return result.data;
    } catch (error) {
      this.log.trace("IndexedDB load failed", { key, error });
      return null;
    }
  }
  async clear(key) {
    try {
      await this.init();
      if (!this.db) {
        return;
      }
      await this.db.delete(STORE_NAME, key);
      this.db.close();
      this.db = null;
      this.log.flow("IndexedDB CLEAR", { key });
    } catch (error) {
      this.log.trace("IndexedDB clear failed", { key, error });
    }
  }
  /**
   * Nuclear option: Delete entire database (used for full reset).
   * @private
   */
  async deleteDatabase() {
    try {
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      await new Promise((resolve, reject) => {
        const req = indexedDB.deleteDatabase(DB_NAME);
        req.onsuccess = () => {
          this.log.flow("IndexedDB database deleted", { db: DB_NAME });
          resolve();
        };
        req.onerror = () => reject(req.error);
        req.onblocked = () => resolve();
      });
    } catch (error) {
      this.log.trace("IndexedDB deleteDatabase failed", { error });
    }
  }
  /**
   * Close the database connection.
   * Used for cleanup in tests.
   */
  async close() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.initPromise = null;
  }
};
export {
  AppLifecycleManager,
  AppLifecycleModule,
  AudioPlane,
  IndexedDBProvider,
  LocalStorageProvider,
  NullPersistenceProvider,
  ObjectiveClock,
  SystemPlane,
  UIPlane
};
